<template>
  <div class="attend-summary-page">
    <van-nav-bar
      :title="(className || '本班') + ' · 考勤'"
      left-arrow
      @click-left="goBack"
      fixed
      placeholder
    />

    <!-- 日期条 -->
    <div class="date-bar">
      <span class="date-nav" :class="{ disabled: !canPrev }" @click="prevDay">‹</span>
      <span class="date-value">{{ currentDate }}</span>
      <span class="date-nav" :class="{ disabled: !canNext }" @click="nextDay">›</span>
      <!-- 日历入口：图标 + 文字整一块可点，绝对定位贴最右 -->
      <div class="date-cal" @click="openCalendar">
        <van-icon name="calendar-o" size="16" color="#52c1b2" />
        <span class="date-cal-text">选择日期</span>
      </div>
    </div>

    <!-- 学生列表 -->
    <div class="student-list">
      <div
        v-for="stu in studentList"
        :key="stu.student_id"
        class="student-card"
        :class="{ 'no-course': !stu.courses || stu.courses.length === 0 }"
      >
        <div class="stu-name">
          {{ stu.name }}
          <van-tag v-if="stu.is_leave == 1" plain class="stu-leave-tag">请假</van-tag>
        </div>
        <template v-if="stu.courses && stu.courses.length">
          <div v-for="(c, idx) in stu.courses" :key="idx" class="course-line">
            <div class="course-left">
              <div class="course-headline">
                <span class="course-name">{{ c.service_class_name }}</span>
                <van-tag :type="courseTypeType(c.course_type)" plain size="medium">
                  {{ courseTypeText(c.course_type) }}
                </van-tag>
                <span class="status-tag" :class="statusClass(c.status)">{{
                  statusText(c.status)
                }}</span>
              </div>
              <span class="course-meta" v-if="c.location">地点: {{ c.location }}</span>
            </div>
            <div class="course-right">
              <!-- 班主任考勤写操作临时禁用：后端按"学生+班级+日期"粒度读写，一个班当天多门课时会串课/误删兄弟课考勤；
                   待后端粒度 bug 修复后，取消本注释块即可恢复按钮 @click="changeAttend(stu, c)"
              <van-button
                size="small"
                plain
                class="attend-btn"
                :class="c.status == 1 ? 'btn-cancel' : 'btn-sign'"
                :loading="signingKey === stu.student_id + '_' + c.service_class_id"
                @click="changeAttend(stu, c)"
                >{{ c.status == 1 ? "取消签到" : "签到" }}</van-button
              >
              -->
            </div>
          </div>
        </template>
        <div v-else class="no-course-text">当日无课后服务</div>
      </div>
      <van-empty v-if="studentList.length === 0" description="暂无学生" />
    </div>

    <!-- 日历弹层：选某天直接切到那天查看/操作（与小程序端一致，范围 60 天前 ~ 今天） -->
    <van-calendar
      v-model:show="showCalendar"
      :min-date="calMinDate"
      :max-date="calMaxDate"
      :default-date="calDefaultDate"
      color="#52c1b2"
      @confirm="onCalConfirm"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { Toast, Dialog } from "vant";
import api from "../service/api";
import { courseTypeText, courseTypeType } from "../utils/courseType";

export default {
  setup() {
    const router = useRouter();
    const route = useRoute();

    const classId = route.query.classId;
    const className = route.query.className || "";

    const currentDate = ref(route.query.date || formatDate(new Date()));
    const studentList = ref([]);

    // 日期边界：不能晚于今天，不能早于 60 天前（班主任可补签 60 天内，与巡课老师回看口径一致）
    const todayStr = formatDate(new Date());
    const minDateStr = formatDate(addDays(new Date(), -60));
    const canNext = computed(() => currentDate.value < todayStr);
    const canPrev = computed(() => currentDate.value > minDateStr);

    const prevDay = () => {
      if (!canPrev.value) return;
      currentDate.value = formatDate(addDays(parseDate(currentDate.value), -1));
      load();
    };
    const nextDay = () => {
      if (!canNext.value) return;
      currentDate.value = formatDate(addDays(parseDate(currentDate.value), 1));
      load();
    };

    // 日历弹层：可选范围与 ‹ › 翻页一致 = 60 天前 ~ 今天（对端小程序同样处理）
    const showCalendar = ref(false);
    const calMinDate = ref(parseDate(minDateStr));
    const calMaxDate = ref(new Date()); // 今天，不可选未来
    const calDefaultDate = ref(parseDate(currentDate.value));

    const openCalendar = () => {
      calDefaultDate.value = parseDate(currentDate.value); // 打开即停在当前查看的那天
      showCalendar.value = true;
    };
    const onCalConfirm = (date) => {
      showCalendar.value = false;
      if (!date) return;
      const d = Array.isArray(date) ? date[0] : date; // 单选中是 Date；兼容返回数组
      const next = formatDate(d);
      if (next === currentDate.value) return; // 同一天不重复加载
      currentDate.value = next;
      load();
    };

    // 查看日期在今天之前 → 未签到计为「缺勤」；今天/未来 → 计为「待签」
    // ponytail: 接口 is_attendance 仅 0/1，无缺勤/待签区分，用日期推算；若后端后续返回明确状态再替换此逻辑
    const isPastDate = computed(() => currentDate.value < todayStr);

    const statusText = (status) => {
      return { 1: "已到", 0: "缺勤", 2: "待签", 3: "请假" }[status] || "未知";
    };
    const statusClass = (status) => {
      return (
        {
          1: "tag-present",
          0: "tag-absent",
          2: "tag-pending",
          3: "tag-leave",
        }[status] || ""
      );
    };
    // 提交锁：signingKey 锁到请求回来，refreshing 表示重查中——期间一律拦下点击
    const signingKey = ref("");
    const refreshing = ref(false);

    const load = async () => {
      try {
        Toast.loading({ message: "加载中...", forbidClick: true });
        const res = await api.studentDateAttendance({
          sid: localStorage.getItem("sid"),
          class_id: classId,
          search_date: currentDate.value,
        });
        Toast.clear();
        if (res.data.code == 1001) {
          const list = res.data.result || [];
          const past = isPastDate.value;
          studentList.value = list.map((s) => ({
            student_id: s.id,
            name: s.name,
            is_leave: s.is_leave,
            courses: (s.courses || []).map((c) => {
              // 课程标签只认考勤数据本身：is_attendance=1 一律显示「已到」，
              // 即使该生 is_leave=1（请假后又到场、老师未取消请假属合法状态，不做推断）；
              // 学生姓名后的「请假」标签仍按 is_leave 独立展示，两者互不覆盖。
              let status;
              if (c.is_attendance == 1) status = 1;
              else if (s.is_leave == 1) status = 3;
              else status = past ? 0 : 2;
              return {
                service_class_id: c.id,
                course_type: c.course_type,
                course_id: c.course_id || "",
                service_time_id: c.service_time_id || "",
                location: c.location,
                service_class_name: c.service_class_name,
                is_attendance: c.is_attendance,
                status,
              };
            }),
          }));
        } else {
          Toast(res.data.message);
        }
      } catch (error) {
        Toast.clear();
        Toast("加载失败，请重试");
      } finally {
        refreshing.value = false;
      }
    };

    /**
     * 班主任代本班学生改某门课的考勤（一次一行 = 一个学生 × 一门课）
     * 已到 → 点 → 改未到(type=2)；非已到 → 点 → 标记已到(今天 type=1 / 过去 type=3 补签)
     */
    const changeAttend = async (stu, c) => {
      const key = `${stu.student_id}_${c.service_class_id}`;
      if (signingKey.value || refreshing.value) {
        Toast("处理中，请稍候");
        return;
      }

      const toAttend = c.status !== 1;
      // 1 正常签到；3补签；  2取消
      const type = toAttend ? (isPastDate.value ? 3 : 1) : 2;
      try {
        await Dialog.confirm({
          title: toAttend ? "标记为已到" : "标记为未到",
          message: `确定将 ${stu.name} 的「${c.service_class_name}」标记为${
            toAttend ? "已到" : "未到"
          }吗？`,
        });
      } catch (e) {
        return; // 用户取消
      }

      signingKey.value = key;
      try {
        Toast.loading({ message: "处理中...", forbidClick: false });
        const res = await api.headTeacheraddStudentAttendance({
          teacher_id: sessionStorage.getItem("userId"),
          course_type: c.course_type,
          school_id: sessionStorage.getItem("tSchoolId"),
          attendance_date: currentDate.value,
          service_class_id: c.service_class_id,
          course_id: c.course_id,
          service_time_id: c.service_time_id,
          type,
          student_id: stu.student_id,
        });
        Toast.clear();
        if (res.data.code == 1001) {
          c.status = toAttend ? 1 : 0;
          refreshing.value = true;
          await load();
        } else {
          Toast(res.data.message || "操作失败");
        }
      } catch (error) {
        Toast.clear();
        Toast("操作失败");
      } finally {
        signingKey.value = "";
      }
    };

    const goBack = () => router.back();

    onMounted(() => {
      load();
    });

    return {
      className,
      currentDate,
      studentList,
      canPrev,
      canNext,
      prevDay,
      nextDay,
      showCalendar,
      calMinDate,
      calMaxDate,
      calDefaultDate,
      openCalendar,
      onCalConfirm,
      courseTypeText,
      courseTypeType,
      statusText,
      statusClass,
      signingKey,
      changeAttend,
      goBack,
    };
  },
};

function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseDate(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
</script>

<style lang="less" scoped>
.attend-summary-page {
  min-height: 100vh;
  background: #f2f2f2;
  padding-bottom: 20px;
}

.date-bar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 14px;
  background: #fff;
  margin-bottom: 8px;
  .date-cal {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    cursor: pointer;
  }
  .date-cal-text {
    margin-left: 4px;
    font-size: 13px;
    color: #52c1b2;
  }
  .date-nav {
    font-size: 22px;
    color: #323233;
    cursor: pointer;
    &.disabled {
      color: #dcdee0;
      cursor: not-allowed;
    }
  }
  .date-value {
    font-size: 15px;
    font-weight: 500;
    color: #323233;
  }
}

.student-list {
  padding: 10px;
}

.student-card {
  background: #fff;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 10px;
  &.no-course {
    background: #f7f8fa;
  }
  .stu-name {
    font-size: 15px;
    font-weight: 500;
    color: #323233;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .stu-leave-tag {
    color: #ff976a;
    background: #fff0e8;
    border-color: #ff976a;
  }
  .course-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 6px 0;
    border-top: 1px solid #f5f5f5;
    .course-left {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;
      flex: 1;
    }
    .course-headline {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }
    .course-name {
      font-size: 14px;
      color: #333;
    }
    .course-meta {
      font-size: 12px;
      color: #999;
    }
  }
  .no-course-text {
    font-size: 12px;
    color: #bbb;
  }
  /* 课程行右侧：操作按钮独占，垂直居中 */
  .course-right {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-shrink: 0;
    margin-right: -16px; /* 抵消 .student-card 的右内边距，让按钮顶到卡片边缘 */
  }
  /* 素净操作按钮：白底、极淡灰边、大方圆角，文字色作唯一点缀，不彩色填充/描边 */
  .attend-btn {
    height: 36px;
    padding: 0 20px;
    font-size: 14px;
    line-height: 1;
    border-radius: 18px;
    &.btn-sign {
      color: #52c1b2;
      border-color: #ebedf0;
      background-color: #fff;
    }
    &.btn-cancel {
      color: #ee0a24;
      border-color: #ebedf0;
      background-color: #fff;
    }
  }
}

.status-tag {
  padding: 2px 8px;
  border-radius: 11px;
  font-size: 11px;
  white-space: nowrap;
  font-weight: 400;
  line-height: 1.5;
  &.tag-present {
    color: #07c160;
    background: #e8f8ef;
  }
  &.tag-absent {
    color: #ee0a24;
    background: #fff0f0;
  }
  &.tag-pending {
    color: #ff976a;
    background: #fff0e8;
  }
  &.tag-leave {
    color: #1989fa;
    background: #e6f1fb;
  }
}
</style>
